/** Pure text helpers shared by the Worker pages and the browser bundle. No Worker or DOM types here. */

/** Two-letter tokens that are words, not acronyms. Lower-cased mid-name, Title-cased first. */
const SMALL2 = new Set(["OF", "BY", "IN", "AT", "TO", "ON", "OR", "AS", "AN", "DE", "DU", "LA", "LE", "EL", "AL", "DA", "DI", "ET"]);
const TITLE2 = new Set(["IT", "MY", "WE", "US", "UP", "NO", "SO", "BE", "DO", "IF", "IS", "ME", "HE", "GO", "OK", "HI", "OX", "AX", "MR", "MS", "DR", "ST", "CO"]);
const SMALL3 = new Set(["AND", "THE", "FOR", "VIA", "PER", "DEL", "VAN", "VON", "DER", "DEN", "UND", "LES", "DES"]);
const ACRONYMS = new Set(["PLC", "LLP", "CIC", "CIO", "BBC", "ITV", "IBM", "BMW", "AXA", "RBS", "TSB", "DHL", "UPS", "USA", "GMC", "HMO", "HGV", "LGV", "EPC", "SEO", "SEN", "CQC", "ATM", "PVC", "LED", "LCD", "GPS", "VAT", "CBD", "DIY", "ESG", "ROI", "CRM", "ERP", "KPI", "NFT", "VPN", "IOT", "API", "PPE", "TBC", "IPA", "UAE", "USB", "CEO", "CIA", "FBI", "MOT", "SUV", "EPS", "HVAC", "HSBC", "NATO", "UEFA", "FIFA", "ASDA", "IKEA", "LIDL", "ALDI", "BUPA", "NCFE", "ACCA", "RICS", "RIBA", "NASA", "TFL", "PCS", "PPL", "BTL", "HMRC", "OFSTED", "UKAS", "ISO"]);
/** Roman numerals as they appear in names (Edward VI, Chapter II, Phase III). Only I/V/X, so LIV/DIV/MIX stay words. */
const ROMAN = /^X{0,3}(IX|IV|V?I{0,3})$/;

/** Title-case one word. After an apostrophe only a one-letter prefix keeps a capital (O'Neill, D'Arcy), so possessives read naturally (Zachary's). */
const titleRun = (w: string): string =>
  w.replace(/[A-Z]+/g, (r, i: number) => (i > 0 && w[i - 1] === "'" && i !== 2 ? r.toLowerCase() : r[0] + r.slice(1).toLowerCase()));

function casePart(part: string, first: boolean): string {
  if (!/[A-Z]/.test(part) || /[a-z]/.test(part)) return part; // digits, symbols, already mixed
  const core = part.replace(/[^A-Z0-9]/g, "");
  if (/\d/.test(core)) return part; // A1, 4X4, B2B
  if (/^[A-Z](\.[A-Z])+\.?$/.test(part)) return part; // C.I.C.
  if (core.length === 1) return part;
  if (core.length >= 2 && ROMAN.test(core)) return part;
  if (ACRONYMS.has(core)) return part;
  if (core === "LTD" || core === "MRS") return titleRun(part);
  if (core.length === 2) return SMALL2.has(core) ? (first ? titleRun(part) : part.toLowerCase()) : TITLE2.has(core) ? titleRun(part) : part;
  if (!/[AEIOUY]/.test(core)) return part; // PPR, NHS, JBC, KPMG: no vowels, read as initials
  if (core.length === 3 && SMALL3.has(core)) return first ? titleRun(part) : part.toLowerCase();
  return titleRun(part);
}

/**
 * Company names arrive in capitals from CH. Render them for reading:
 * "THE WORSHIPFUL COMPANY OF CUTLERS" → "The Worshipful Company of Cutlers", "PPR HOLDINGS LTD" → "PPR Holdings Ltd",
 * "DAR AL FARAH LTD" → "Dar al Farah Ltd", "C.I.C." and "VI" stay as they are, "LIMITED(THE)" → "Limited (The)".
 */
export function displayName(name: string): string {
  const spaced = name.trim().replace(/([,;])(?=\S)/g, "$1 ").replace(/(\S)\(/g, "$1 (").replace(/\)(?=[A-Za-z])/g, ") ").replace(/\s+/g, " ");
  return spaced
    .split(" ")
    .map((tok, i) => tok.split(/([&/])/).map((part) => casePart(part, i === 0 || part.startsWith("("))).join(""))
    .join(" ");
}

export const slugify = (s: string): string =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** "2026-09-23" → "23/09/2026" */
export const ukDate = (iso: string | null | undefined): string =>
  iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** "2026-09-23" → "23 September 2026" */
export const longDate = (iso: string | null | undefined): string =>
  iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}` : "—";
export const monthName = (m: number): string => MONTHS[m - 1] ?? "";

/** Whole years between two ISO dates (birthday-aware). */
export function yearsBetween(from: string, to: string): number {
  const a = new Date(from), b = new Date(to);
  let y = b.getUTCFullYear() - a.getUTCFullYear();
  if (b.getUTCMonth() < a.getUTCMonth() || (b.getUTCMonth() === a.getUTCMonth() && b.getUTCDate() < a.getUTCDate())) y--;
  return Math.max(0, y);
}

export function ageText(from: string | null | undefined, to: string): string {
  if (!from) return "";
  const y = yearsBetween(from, to);
  if (y >= 1) return `${y} year${y === 1 ? "" : "s"}`;
  const days = Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000));
  if (days < 1) return "today";
  if (days < 31) return `${days} day${days === 1 ? "" : "s"}`;
  const months = Math.round(days / 30.4);
  return `${months} month${months === 1 ? "" : "s"}`;
}

/** Money in pence → "£1.2m", "£350k", "£4,200". Negative values keep the sign. */
export function gbp(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return "—";
  const sign = pence < 0 ? "−" : "";
  const p = Math.abs(pence) / 100;
  if (p >= 1e9) return `${sign}£${(p / 1e9).toFixed(p >= 1e10 ? 0 : 1)}bn`;
  if (p >= 1e6) return `${sign}£${(p / 1e6).toFixed(p >= 1e7 ? 0 : 1)}m`;
  if (p >= 1e5) return `${sign}£${Math.round(p / 1e3)}k`;
  return `${sign}£${Math.round(p).toLocaleString("en-GB")}`;
}

export const nf = (n: number | null | undefined): string => (n ?? 0).toLocaleString("en-GB");

/** Tokens that don't say anything about what a company does. Used by the trend spotter. */
export const NAME_STOPWORDS = new Set([
  "LTD", "LIMITED", "LTD.", "THE", "AND", "OF", "CO", "CO.", "COMPANY", "GROUP", "HOLDINGS", "SERVICES", "UK", "PLC", "LLP",
  "CIC", "C.I.C.", "SOLUTIONS", "ENTERPRISES", "INTERNATIONAL", "LIMITED.", "PARTNERSHIP", "IN", "FOR", "TO", "A", "AN", "BY",
  "ON", "AT", "WITH", "ITS", "ONE", "TWO", "NEW", "LONDON", "GLOBAL", "MANAGEMENT", "CONSULTING", "CONSULTANCY", "CONSULTANTS",
  "TRADING", "INVESTMENTS", "PROPERTIES", "PROPERTY", "DEVELOPMENTS", "VENTURES", "PARTNERS", "ASSOCIATES", "BROTHERS", "SONS",
  "T/A", "TA", "MR", "MRS", "MS", "DR", "ST", "ST.", "NO", "NO.", "&",
]);
